import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_TEAMS } from '@/lib/constants/api'
import updateStatus from '@/store/helpers/updateStatus'
import createPaginatedGetAction from '@/store/helpers/createPaginatedGetAction'
import { notifyError, notifySuccess } from '@/lib/notifications'
import buildInitialState from '@/store/helpers/buildInitialState'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'
import getTeamModel from '@/store/modules/models/team'

const teams = {
  namespaced: true,
  state: buildInitialState({
    teamsList: [],
    totalRecords: 0,
    ...getTeamModel()
  }),
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    setTeamsList: (state, teamsList) => {
      state.teamsList = teamsList
      state.totalRecords = Array.isArray(teamsList) ? teamsList.length : 0
    },
    setPaginatedTeams (state, { result, totalRecords }) {
      state.teamsList = result
      state.totalRecords = totalRecords
    },
    setTeam (state, team) {
      state.id = team.id
      state.teamName = team.teamName
      state.managerId = team.managerId
      state.managerName = team.managerName
      state.teamMembers = team.teamMembers
    }
  },
  actions: {
    getTeamsWithFiltersPaginated ({ dispatch }, params = {}) {
      return dispatch('getTeamsPaginated', params)
    },
    getTeamsPaginated: createPaginatedGetAction(PATH_TEAMS, 'setPaginatedTeams', 'Unable to select teams'),
    getTeams: ({ commit, dispatch }, filters = {}) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Getting teams, please wait...' })

      return api.doGet({ path: PATH_TEAMS, parameters: filters })
        .then(json => json.data)
        .then(teamsList => {
          commit('updateStatus', { status: COMPLETE })
          commit('setTeamsList', teamsList)
          return teamsList
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to select teams')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    getTeamOptions ({ dispatch }, filters = {}) {
      return api.doGet({ path: PATH_TEAMS, parameters: filters })
        .then(json => json.data || [])
        .catch(() => {
          notifyError(dispatch, 'Unable to load teams')
          return []
        })
    },
    saveTeam ({ commit, dispatch }, team) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Saving team, please wait...' })
      return api.doPost({ path: PATH_TEAMS, data: team })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            throw new Error('Unable to save team')
          }

          notifySuccess(dispatch, 'Team saved')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to save team')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    updateTeam ({ commit, dispatch }, team) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Updating team, please wait...' })
      return api.doPut({ path: PATH_TEAMS, data: team })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            throw new Error('Unable to update team')
          }

          notifySuccess(dispatch, 'Team updated')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to update team')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    deleteTeam ({ commit, dispatch }, id = null) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Deleting team, please wait...' })
      return api.doDelete({
        path: `${PATH_TEAMS}/${id}`
      })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            throw new Error('Unable to delete team')
          }

          notifySuccess(dispatch, 'Team deleted')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to delete team')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    }
  }
}

export default teams
